/**
 * "Névtáblával várakozol?" — asked once, at an airport, while the driver is
 * standing at the pickup waiting for someone who has not come out.
 *
 * WHY IT IS HERE AND NOT SOMEWHERE OF ITS OWN. The photo of the name sign
 * held up in arrivals is not a nicety: it is the one thing that shows the
 * driver was VISIBLY there, which is exactly what a contested no-show turns
 * on. Taking it afterwards, from inside the no-show sheet, is too late —
 * by then the driver has usually walked off. So it is asked for while the
 * waiting is still happening, and handed to the no-show sheet if one is
 * ever filed (TripDetail holds it; NoShowSheet attaches it).
 *
 * IT IS TWO QUESTIONS, AND THE SECOND ONE DECIDES WHETHER A PHOTO IS ASKED
 * FOR AT ALL. "Igen" is followed by "Melyik névtáblával?" — the app's own
 * sign (Portal/NameSign.jsx, the full-screen "Névtábla") or the driver's
 * own board.
 *
 *   Appos névtábla → NO photo. WE RENDERED IT, so we know exactly what it
 *                    displayed and that it obeyed the partner's white-label
 *                    rule. That text is better evidence than a photograph
 *                    of it, and asking a driver to photograph their own
 *                    phone would be absurd.
 *   Saját          → the photo, because a board we did not draw is the only
 *                    sign we cannot otherwise see.
 *
 * ALL THREE ANSWERS ARE RECORDED, including "Nem". "The driver was standing
 * there holding a sign reading KOVÁCS" is the fact a partner disputes
 * hardest, and before this only the PHOTO survived — the answer itself was
 * thrown away while the bytes were kept.
 *
 * It is a prompt, never a gate. "Nem" dismisses it for the rest of the
 * screen's life, and a no-show can still be filed with no photo at all
 * ("lehetőség szerint tölts fel fényképet").
 *
 * THE ANSWER IS THE SERVER'S, NOT THIS COMPONENT'S. It is posted as its own
 * driver event the moment it is given (`name_sign_answered`) and comes back
 * on the trip, so a reload, an app restart or a new phone all show what the
 * driver already said — and a trip that ends normally still carries the
 * record. A driver may change their mind (put the sign away, or produce one
 * later): the latest answer wins and every one stays in the event log.
 *
 * The PHOTO still rides in sessionStorage until a no-show is filed. That is
 * an interim: uploading it the moment it is taken would mean an S3 write on
 * a path that does not exist for a trip with no claim on it, which is a
 * bigger change than it looks. Storage is always allowed to fail — private
 * browsing, a full quota, an iOS Home Screen app with its own empty storage
 * — and when it does the driver is simply asked again.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { downscaleImage } from '../imageDownscale';
import { NAME_SIGN_ASK_COPY } from '../portalFormat';
import { CameraIcon, NameSignIcon } from './Icons';

const STORE_PREFIX = 'ph_portal_sign_photo:';

/** The photo this device remembers for one booking, or null. */
export const readSignPhoto = (bookingId) => {
  try {
    return JSON.parse(window.sessionStorage.getItem(`${STORE_PREFIX}${bookingId}`)) || null;
  } catch {
    return null;
  }
};

const rememberSignPhoto = (bookingId, photo) => {
  try {
    window.sessionStorage.setItem(`${STORE_PREFIX}${bookingId}`, JSON.stringify(photo));
  } catch {
    // Quota, private browsing, no storage at all. The photo still works for
    // this screen; only surviving a reload is lost.
  }
};

export default function NameSignAsk({
  bookingId, signName = '', photo = null, answer: recorded = null, onPhoto, onAnswer,
  copy = NAME_SIGN_ASK_COPY,
}) {
  // Only the HALF-answered state is local: "Igen" on its own says nothing
  // worth recording, so it is the one thing the server has no opinion on.
  const [pending, setPending] = useState(false);

  /**
   * Hand the answer up. "Igen" on its own says nothing yet — whose sign is
   * the next question — so only the three real answers are reported.
   *
   * It is worth more than the photo it may lead to: the no-show evidence
   * keeps it, and the WhatsApp the driver sends the passenger can say what
   * the sign READS, which is the one thing that ends a search in an
   * arrivals hall.
   */
  const answered = useCallback((value) => {
    setPending(false);
    if (!onAnswer) return;
    if (value === 'no') onAnswer({ kind: 'none' });
    // Only our own sign carries a `displayed`: it is the only one we drew.
    if (value === 'app') onAnswer({ kind: 'app', ...(signName ? { displayed: signName } : {}) });
    if (value === 'own') onAnswer({ kind: 'own' });
  }, [onAnswer, signName]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // A photo remembered from before a reload goes straight back to the trip
  // screen, so the no-show sheet finds it without asking again.
  useEffect(() => {
    if (photo || !bookingId) return;
    const remembered = readSignPhoto(bookingId);
    if (remembered) onPhoto(remembered);
    // Only on mount / when the booking changes. `onPhoto` and `photo` are
    // deliberately out: `onPhoto` is a fresh closure on every render of the
    // trip screen, and re-running this after it has handed the photo over
    // would be a loop.
  }, [bookingId]); // eslint-disable-line

  const take = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!file) return;

    setProcessing(true);
    setError('');
    try {
      const taken = await downscaleImage(file);
      rememberSignPhoto(bookingId, taken);
      onPhoto(taken);
    } catch {
      setError(copy.unreadable);
    } finally {
      setProcessing(false);
    }
  }, [bookingId, copy, onPhoto]);

  // Recorded as "no sign": nothing more to ask, on this render or any
  // later one, on this phone or another.
  const kind = recorded?.kind || '';
  if (kind === 'none') return null;

  return (
    <div className="pp-signask" data-testid="name-sign-ask">
      {photo ? (
        <div className="pp-photo__taken">
          <img className="pp-photo__thumb" src={`data:${photo.type};base64,${photo.data}`} alt={copy.alt} />
          <div className="pp-photo__side">
            <span className="pp-photo__ok">{copy.saved}</span>
            <label htmlFor="pp-sign-photo" className={`pp-btn pp-btn--outline${processing ? ' pp-btn--inert' : ''}`}>
              <CameraIcon size={18} />
              {processing ? copy.processing : copy.retake}
            </label>
          </div>
        </div>
      ) : (
        <>
          <div className="pp-signask__row">
            <span className="pp-signask__question">
              <NameSignIcon size={18} />
              {pending || kind ? copy.kindQuestion : copy.question}
            </span>
            {!pending && !kind ? (
              <span className="pp-actions pp-signask__answers">
                <button type="button" className="pp-btn pp-btn--outline" onClick={() => setPending(true)}>
                  {copy.yes}
                </button>
                <button type="button" className="pp-btn pp-btn--outline" onClick={() => answered('no')}>
                  {copy.no}
                </button>
              </span>
            ) : null}
            {pending && !kind ? (
              <span className="pp-actions pp-signask__answers">
                <button type="button" className="pp-btn pp-btn--outline" onClick={() => answered('app')}>
                  {copy.app}
                </button>
                <button type="button" className="pp-btn pp-btn--outline" onClick={() => answered('own')}>
                  {copy.own}
                </button>
              </span>
            ) : null}
          </div>

          {/* Our own sign: recorded, and nothing more asked of the driver. */}
          {kind === 'app' ? (
            <p className="pp-signask__why" data-testid="name-sign-app">{copy.appChosen(signName)}</p>
          ) : null}

          {kind === 'own' ? (
            <>
              <p className="pp-signask__why">{copy.why}</p>
              <label
                htmlFor="pp-sign-photo"
                className={`pp-btn pp-btn--dashed pp-btn--block${processing ? ' pp-btn--inert' : ''}`}
              >
                <CameraIcon size={18} />
                {processing ? copy.processing : copy.upload}
              </label>
            </>
          ) : null}
        </>
      )}

      {/*
        One input for both the first shot and a retake, kept in the
        accessibility tree and opened by a <label> — the only way that works
        on every mobile browser (see ExpenseSheet for the full story).
        RENDERED ONLY WHEN ONE OF THOSE LABELS IS ON SCREEN: a visually
        hidden file input with nothing pointing at it is a control a screen
        reader announces and nobody can name (axe "label", critical), and
        while the question is still being asked there is nothing to take a
        picture of yet.
      */}
      {photo || kind === 'own' ? (
        <input
          id="pp-sign-photo"
          className="visually-hidden"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={take}
          disabled={processing}
        />
      ) : null}

      {error ? <div className="pp-form__error" role="alert">{error}</div> : null}
    </div>
  );
}
